import { TitleCasePipe } from '@angular/common';
import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { MatCheckboxChange, MatDialog, MatSnackBar } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { combineLatest } from 'rxjs';
import { CopyToEmrModalComponent } from 'src/app/copy-to-emr-modal/copy-to-emr-modal.component';
import { Assessment, AssessmentService } from 'src/app/core/api/assessment.service';
import { PatientService } from 'src/app/core/api/patient.service';
import {
  QuestionnaireRequest,
  QuestionnaireRequestService
} from 'src/app/core/api/questionnaire-request.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { StaffPermissionsService } from 'src/app/core/services/staff-permissions.service';
import { LocaleDatePipe } from 'src/app/shared/shared-pipes/locale-date.pipe';
import { EncountersToCompare } from '../../encounters-compare-modal/encounters-compare-modal.component';
import {
  QuestionnaireResponseModalComponent,
  QuestionnaireResponseModalData
} from './../../../shared/consult-forms/questionnaire-response-modal/questionnaire-response-modal.component';
import { hasImpressionsSectionContent } from 'src/app/shared/impressions/impressions-sections.util';
import { RecordDetailHost } from '../record-detail-panel/record-detail-host';
import { SideSheetService } from '../../../shared/side-sheet/side-sheet.service';
import { RecordDetail } from '../record-detail-panel/record-detail.model';
import { ChipColour } from 'src/app/shared/chip/chip.component';
import {
  AssessmentRow,
  AssessmentScoreChip,
  LinkedQuestionnaireRow
} from './assessment-column.model';
import {
  AssessmentDetailAction,
  AssessmentDetailSheetComponent
} from './assessment-detail-sheet.component';

@Component({
  selector: 'csi-assessments-table',
  templateUrl: './assessments-table.component.html',
  styleUrls: ['./assessments-table.component.scss'],
  providers: [TitleCasePipe, LocaleDatePipe]
})
export class AssessmentsTableComponent extends RecordDetailHost<string>
  implements OnInit, OnChanges, OnDestroy, DoCheck {
  @Input() encountersToCompare: EncountersToCompare = [];
  @Output() encountersToCompareChange = new EventEmitter<EncountersToCompare>();

  public readonly MAX_COMPARE = 4;
  public readonly translocoFileKey = 'dashboard.patients-dashboard.';

  public assessmentRows: AssessmentRow[] = [];

  // The old template bound [dataSource] straight to patientService.assessments, so saving an
  // assessment showed up immediately. This view-model is a snapshot, so watch the array identity
  // to keep that behaviour — PatientService exposes no change stream to subscribe to.
  private lastSeenPatientId: string = null;
  private lastSeenAssessments: Assessment[] = [];

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private dialog: MatDialog,
    public patientService: PatientService,
    public assessmentService: AssessmentService,
    public staffPermissionsService: StaffPermissionsService,
    private questionnaireRequestService: QuestionnaireRequestService,
    private snackBar: MatSnackBar,
    private appStateService: AppStateService,
    private localeDatePipe: LocaleDatePipe,
    sideSheetService: SideSheetService
  ) {
    super(sideSheetService);
  }

  ngOnInit() {
    this.appStateService.currentPatient$.pipe(untilDestroyed(this)).subscribe(patient => {
      const patientId = patient ? patient.id : null;
      if (patientId === this.lastSeenPatientId) {
        return;
      }
      this.lastSeenPatientId = patientId;
      this.closeDetail();
      this.encountersToCompareChange.emit([]);
    });

    combineLatest([
      this.appStateService.currentPatient$,
      this.appStateService.isPatientAppActive$
    ])
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateAssessmentsWithAppStatus();
        this.refreshRows();
      });
  }

  ngOnChanges() {
    this.refreshSelectionFlags();
  }

  ngDoCheck() {
    if (this.hasAssessmentListChanged()) {
      this.refreshRows();
    }
  }

  private hasAssessmentListChanged(): boolean {
    const assessments = this.patientService.assessments || [];
    if (assessments.length !== this.lastSeenAssessments.length) {
      return true;
    }
    return assessments.some(
      (assessment, index) => assessment !== this.lastSeenAssessments[index]
    );
  }

  ngOnDestroy() {
    this.closeDetail();
  }

  public refreshRows() {
    const assessments = this.patientService.assessments || [];
    this.lastSeenAssessments = assessments.slice();
    const latestAssessmentId = this.findLatestAssessmentId(assessments);
    this.assessmentRows = assessments.map(assessment =>
      this.buildAssessmentRow(assessment, assessment.id === latestAssessmentId)
    );
  }

  private findLatestAssessmentId(assessments: Assessment[]): string {
    let latestAssessment = null;
    assessments.forEach(assessment => {
      if (
        !latestAssessment ||
        this.toComparableTime(assessment.createdAt) >
          this.toComparableTime(latestAssessment.createdAt)
      ) {
        latestAssessment = assessment;
      }
    });
    return latestAssessment ? latestAssessment.id : null;
  }

  private refreshSelectionFlags() {
    const selectedIds = (this.encountersToCompare || [])
      .filter(Boolean)
      .map(comparingEncounter => comparingEncounter.id);
    this.assessmentRows = this.assessmentRows.map(assessmentRow => ({
      ...assessmentRow,
      isSelectedForCompare: selectedIds.indexOf(assessmentRow.assessment.id) > -1
    }));
  }

  public updateAssessmentsWithAppStatus() {
    if (this.patientService.assessments) {
      this.patientService.assessments.forEach(encounter => {
        if (encounter && encounter.id) {
          encounter['isCurrentInApp'] = this.isCurrentAssessmentInApp(encounter.id);
        }
      });
    }
  }

  public selectAssessment(assessmentRow: AssessmentRow) {
    const sheetReference = this.toggleDetail<AssessmentRow, AssessmentDetailAction>(
      assessmentRow.assessment.id,
      () => assessmentRow,
      AssessmentDetailSheetComponent,
      { width: assessmentRow.hasClinicalNarrative ? '58rem' : '30rem' }
    );
    if (!sheetReference) {
      return;
    }
    // Navigation and compare state belong to this column: a body created by the dialog resolves
    // ActivatedRoute to the root route, so it cannot navigate relative to the encounter outlet.
    sheetReference.afterClosed().subscribe(action => {
      if (action === 'openAssessment') {
        this.openAssessment(assessmentRow);
        return;
      }
      if (action === 'compare') {
        this.toggleCompare(assessmentRow);
      }
    });
  }

  public openAssessment(assessmentRow: AssessmentRow) {
    this.router.navigate([{ outlets: { encounters: assessmentRow.assessment.id } }], {
      relativeTo: this.route.parent
    });
  }

  public goToMobileMediaWizard(assessmentId: string) {
    this.router.navigateByUrl(`/${assessmentId}/mobile-media-wizard`);
  }

  public showQuestionnaireResponses(questionnaires: Partial<QuestionnaireRequest>[]) {
    this.sideSheetService.open<
      QuestionnaireResponseModalComponent,
      QuestionnaireResponseModalData,
      void
    >(
      QuestionnaireResponseModalComponent,
      QuestionnaireResponseModalComponent.buildDialogData(
        null,
        questionnaires,
        this.patientService.patient.id
      )
    );
  }

  public openCopyModal(assessment: Assessment, assessmentMethod: string) {
    this.dialog.open(CopyToEmrModalComponent, {
      data: {
        showAssessmentList: false,
        assessment: assessment,
        assessmentMethod: assessmentMethod
      },
      width: '95vw',
      minWidth: '50rem',
      height: '92vh'
    });
  }

  public encounterSelectionOnChange(change: MatCheckboxChange, encounter: Assessment) {
    const currentSelection = this.encountersToCompare || [];

    if (change.checked) {
      if (currentSelection.length >= this.MAX_COMPARE) {
        change.source.checked = false;
        this.showCompareLimitReached();
        return;
      }
      this.setEncountersToCompare([...currentSelection, encounter]);

      if (this.encountersToCompare.length === this.MAX_COMPARE) {
        this.snackBar.open(`You’ve reached the maximum of ${this.MAX_COMPARE} assessments.`, 'OK', {
          duration: 3500
        });
      }
      return;
    }

    this.setEncountersToCompare(
      currentSelection.filter(comparingEncounter => comparingEncounter.id !== encounter.id)
    );
  }

  /**
   * Compare toggled from the detail sheet, which has no checkbox to report a change. Routed
   * through the same setEncountersToCompare as the checkbox path so both stay in step.
   */
  public toggleCompare(assessmentRow: AssessmentRow) {
    const currentSelection = this.encountersToCompare || [];
    const assessment = assessmentRow.assessment;

    if (assessmentRow.isSelectedForCompare) {
      this.setEncountersToCompare(
        currentSelection.filter(comparingEncounter => comparingEncounter.id !== assessment.id)
      );
      return;
    }

    if (currentSelection.length >= this.MAX_COMPARE) {
      this.showCompareLimitReached();
      return;
    }
    this.setEncountersToCompare([...currentSelection, assessment]);
  }

  private showCompareLimitReached() {
    this.snackBar.open(
      `You can compare up to ${this.MAX_COMPARE} assessments. Deselect one to add another.`,
      'OK',
      { duration: 3500 }
    );
  }

  private setEncountersToCompare(updatedSelection: EncountersToCompare) {
    this.encountersToCompare = updatedSelection;
    this.encountersToCompareChange.emit(updatedSelection);
    this.refreshSelectionFlags();
  }

  public isCurrentAssessmentInApp(assessmentId: string): boolean {
    if (!this.appStateService.getIsPatientAppActive()) {
      return false;
    }

    const enrollmentConfig =
      this.patientService.patient && this.patientService.patient.patientAppEnrollmentConfig;
    if (!enrollmentConfig) {
      return false;
    }
    let parsedConfig: any = enrollmentConfig;
    if (typeof parsedConfig === 'string') {
      try {
        parsedConfig = JSON.parse(parsedConfig);
      } catch (parseError) {
        return false;
      }
    }
    if (parsedConfig && parsedConfig.assessmentHistory && Array.isArray(parsedConfig.assessmentHistory)) {
      return parsedConfig.assessmentHistory.some((historyItem: any) => {
        return (
          historyItem &&
          historyItem.id === assessmentId &&
          historyItem.status &&
          historyItem.status.toString().toLowerCase() === 'current'
        );
      });
    }
    return false;
  }

  public trackByAssessmentId(index: number, assessmentRow: AssessmentRow): string {
    return assessmentRow.assessment.id;
  }

  public trackByScoreChip(index: number, scoreChip: AssessmentScoreChip): string {
    return scoreChip.instrumentName + ':' + index;
  }

  private buildAssessmentRow(assessment: Assessment, isLatest: boolean): AssessmentRow {
    const assessmentBody = this.parseAssessmentBody(assessment);
    const category = assessmentBody.assessmentForm && assessmentBody.assessmentForm.category;
    const severity = (category && category.severity) || category;
    const severityText = severity ? String(severity) : '';
    const categoryType = category && category.type ? String(category.type) : '';
    const categoryLabel = categoryType
      ? (severityText + ' ' + categoryType).toUpperCase()
      : severityText;
    const assessmentMethod =
      assessmentBody.dryEyeForm && assessmentBody.dryEyeForm.assessmentMethod
        ? assessmentBody.dryEyeForm.assessmentMethod
        : '';

    const allLinkedQuestionnaires = this.toLinkedQuestionnaires(assessment);
    const linkedQuestionnaires = this.toLatestQuestionnairePerType(allLinkedQuestionnaires);
    const answeredLinkedQuestionnaires = linkedQuestionnaires.filter(
      questionnaire => questionnaire && questionnaire.response
    );

    return {
      assessment: assessment,
      createdAt: assessment.createdAt,
      updatedAt: assessment.updatedAt,
      categoryLabel: categoryLabel,
      hasCategory: !!severityText,
      isSevereCategory: severityText.toLowerCase() === 'severe',
      methodLabel: this.assessmentService.encounterMethodFriendlyName[assessmentMethod] || '',
      assessmentMethod: assessmentMethod,
      scoreChips: this.buildScoreChips(linkedQuestionnaires),
      isLatest: isLatest,
      isSelectedForCompare: this.isEncounterSelected(assessment),
      isCurrentInApp: !!assessment['isCurrentInApp'],
      hasLinkedQuestionnaires: linkedQuestionnaires.length > 0,
      hasClinicalNarrative: this.hasClinicalNarrative(assessmentBody),
      hasLinkedQuestionnaireResponses: answeredLinkedQuestionnaires.length > 0,
      linkedQuestionnaires: linkedQuestionnaires,
      answeredLinkedQuestionnaires: answeredLinkedQuestionnaires,
      linkedQuestionnaireRows: this.buildLinkedQuestionnaireRows(linkedQuestionnaires)
    };
  }

  private readonly narrativePlanFields = [
    'recommendedProcedures',
    'recommendedRxEyeDrops',
    'recommendedEyeDrops',
    'recommendedExercises',
    'recommendedPlanByDoctor',
    'goalsForPatient',
    'treatmentPerformed'
  ];

  private hasClinicalNarrative(assessmentBody: any): boolean {
    const assessmentForm = assessmentBody && assessmentBody.assessmentForm;
    if (!assessmentForm) {
      return false;
    }
    const impressions = assessmentForm.impressions;
    if (
      hasImpressionsSectionContent(impressions, 'interpretation') ||
      hasImpressionsSectionContent(impressions, 'riskFactors')
    ) {
      return true;
    }
    if (assessmentForm.doctorNotes) {
      return true;
    }
    return this.narrativePlanFields.some(planField => !!assessmentForm[planField]);
  }

  private isEncounterSelected(assessment: Assessment): boolean {
    return (this.encountersToCompare || []).some(
      comparingEncounter => comparingEncounter && comparingEncounter.id === assessment.id
    );
  }

  private toLinkedQuestionnaires(assessment: Assessment): Partial<QuestionnaireRequest>[] {
    const questionnaireConnection = assessment ? assessment.questionnaireRequests : null;
    const linkedItems =
      questionnaireConnection && questionnaireConnection.items
        ? (questionnaireConnection.items as Partial<QuestionnaireRequest>[])
        : [];
    return linkedItems.filter(Boolean);
  }

  private toLatestQuestionnairePerType(
    linkedQuestionnaires: Partial<QuestionnaireRequest>[]
  ): Partial<QuestionnaireRequest>[] {
    const latestByType: { [key: string]: Partial<QuestionnaireRequest> } = {};
    linkedQuestionnaires.forEach(questionnaire => {
      const questionnaireType = this.questionnaireRequestService.getQuestionnaireType(
        questionnaire
      );
      const currentLatest = latestByType[questionnaireType];
      if (!currentLatest || this.isMoreRecentQuestionnaire(questionnaire, currentLatest)) {
        latestByType[questionnaireType] = questionnaire;
      }
    });
    return Object.keys(latestByType).map(questionnaireType => latestByType[questionnaireType]);
  }

  private isMoreRecentQuestionnaire(
    questionnaire: Partial<QuestionnaireRequest>,
    currentLatest: Partial<QuestionnaireRequest>
  ): boolean {
    if (!!questionnaire.completedAt !== !!currentLatest.completedAt) {
      return !!questionnaire.completedAt;
    }
    return (
      this.toComparableTime(questionnaire.completedAt || questionnaire.createdAt) >
      this.toComparableTime(currentLatest.completedAt || currentLatest.createdAt)
    );
  }

  private toComparableTime(dateValue: string): number {
    const parsedTime = new Date(dateValue).getTime();
    return isNaN(parsedTime) ? 0 : parsedTime;
  }

  private buildLinkedQuestionnaireRows(
    linkedQuestionnaires: Partial<QuestionnaireRequest>[]
  ): LinkedQuestionnaireRow[] {
    return linkedQuestionnaires.map(questionnaire => ({
      questionnaireId: questionnaire.id,
      questionnaire: questionnaire,
      completedAt: questionnaire.completedAt
    }));
  }

  private buildScoreChips(
    linkedQuestionnaires: Partial<QuestionnaireRequest>[]
  ): AssessmentScoreChip[] {
    const scoreChips: AssessmentScoreChip[] = [];
    linkedQuestionnaires.forEach(questionnaire => {
      this.questionnaireRequestService
        .getInstrumentScores(questionnaire)
        .forEach(instrumentScore => {
          const hasScore = !isNaN(instrumentScore.score);
          const hasRiskVerdict = !!this.questionnaireRequestService.instrumentRiskThresholds[
            instrumentScore.type
          ];
          const isAtRisk = hasScore && instrumentScore.isAtRisk;
          scoreChips.push({
            instrumentName: instrumentScore.friendlyName || '',
            scoreLabel: hasScore ? String(instrumentScore.score) : '—',
            chipColour: this.getScoreChipColour(hasScore && hasRiskVerdict, isAtRisk),
            isAtRisk: hasRiskVerdict && isAtRisk
          });
        });
    });
    return this.sortAtRiskFirst(scoreChips);
  }

  private sortAtRiskFirst(scoreChips: AssessmentScoreChip[]): AssessmentScoreChip[] {
    const atRiskChips = scoreChips.filter(scoreChip => scoreChip.isAtRisk);
    const remainingChips = scoreChips.filter(scoreChip => !scoreChip.isAtRisk);
    return atRiskChips.concat(remainingChips);
  }

  private getScoreChipColour(hasScore: boolean, isAtRisk: boolean): ChipColour {
    if (!hasScore) {
      return 'grey-no-outline';
    }
    return isAtRisk ? 'red-no-outline' : 'green-no-outline';
  }

  private buildAssessmentDetail(assessmentRow: AssessmentRow): RecordDetail {
    const createdAtLabel = this.formatDate(assessmentRow.createdAt);
    const updatedAtLabel = this.formatDate(assessmentRow.updatedAt);

    return {
      recordType: assessmentRow.methodLabel || 'Assessment',
      title: createdAtLabel,
      subtitle: assessmentRow.scoreChips.length + ' scores linked',
      summaryValue: assessmentRow.categoryLabel || '—',
      isSummaryAtRisk: assessmentRow.isSevereCategory,
      isSummaryMuted: !assessmentRow.hasCategory,
      sections: [
        {
          title: 'Scores used',
          note: assessmentRow.scoreChips.length === 0 ? 'No questionnaires are linked.' : null,
          rows: assessmentRow.scoreChips.map(scoreChip => ({
            label: scoreChip.instrumentName,
            value: scoreChip.scoreLabel
          }))
        },
        {
          title: 'Record',
          rows: [
            { label: 'Created', value: createdAtLabel, isValueMuted: true },
            { label: 'Last updated', value: updatedAtLabel, isValueMuted: true }
          ]
        }
      ]
    };
  }

  private parseAssessmentBody(assessment: Assessment): any {
    if (!assessment || !assessment.body) {
      return {};
    }
    try {
      return typeof assessment.body === 'string' ? JSON.parse(assessment.body) : assessment.body;
    } catch (parseError) {
      return {};
    }
  }

  private formatDate(isoDate: string): string {
    return this.localeDatePipe.transform(isoDate, 'MMM d, y') || '—';
  }
}
